import { useCallback, useEffect, useState } from "react";
import { BellRing, Copy, KeyRound, Laptop, LogOut, MonitorSmartphone, QrCode, ShieldCheck, ShieldOff } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { NotificationSettings } from "@/components/NotificationSettings";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

function formatStamp(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export const RecoveryCodes = ({ codes, onDone }) => (
  <div className="recovery-codes" data-testid="recovery-codes">
    <p><strong>Save these recovery codes now.</strong> Each works once if you lose your phone. They will not be shown again.</p>
    <ol>{codes.map((code) => <li key={code}><code>{code}</code></li>)}</ol>
    <div className="policy-actions">
      <button className="button button-secondary" data-testid="recovery-codes-copy" type="button" onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}><Copy size={14} /> Copy</button>
      <button className="button button-primary" data-testid="recovery-codes-done" type="button" onClick={onDone}>I saved them</button>
    </div>
  </div>
);

export const TwoFactorSetup = ({ onEnabled, required }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function begin() {
    setBusy(true);
    setError("");
    try {
      setSetup((await api.post("/auth/2fa/setup")).data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function enable(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/auth/2fa/enable", { code });
      setCodes(response.data.recovery_codes);
      onEnabled?.(response.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (codes) return <RecoveryCodes codes={codes} onDone={() => { setCodes(null); setSetup(null); onEnabled?.(null, true); }} />;
  return (
    <div className="two-factor-setup" data-testid="two-factor-setup">
      {!setup ? (
        <>
          <p>{required ? "Two-factor sign-in is required for your role." : "Add a second step at sign-in with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Authy)."}</p>
          <button className="button button-primary" data-testid="two-factor-begin" disabled={busy} type="button" onClick={begin}><QrCode size={14} /> Set up authenticator app</button>
        </>
      ) : (
        <form className="two-factor-form" onSubmit={enable}>
          <ol className="two-factor-steps">
            <li>Scan this QR code with your authenticator app.<br /><img alt="Authenticator QR code" className="two-factor-qr" data-testid="two-factor-qr" src={setup.qr_data_url} /></li>
            <li>Can't scan? Enter this key manually: <code data-testid="two-factor-secret">{setup.secret}</code></li>
            <li>
              <label>Enter the 6-digit code from the app
                <input data-testid="two-factor-code-input" inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" required value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
            </li>
          </ol>
          <button className="button button-primary" data-testid="two-factor-enable" disabled={busy || code.replace(/\s/g, "").length < 6} type="submit"><ShieldCheck size={14} /> Turn on two-factor sign-in</button>
        </form>
      )}
      {error && <p className="form-message error-message" data-testid="two-factor-error">{error}</p>}
    </div>
  );
};

function DisableTwoFactor({ onDisabled }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ password: "", code: "" });
  const [error, setError] = useState("");
  async function submit(event) {
    event.preventDefault();
    setError("");
    try {
      await api.post("/auth/2fa/disable", form);
      setOpen(false);
      onDisabled();
    } catch (e) {
      setError(formatApiError(e));
    }
  }
  if (!open) return <button className="table-edit-button table-delete-button" data-testid="two-factor-disable-open" type="button" onClick={() => setOpen(true)}><ShieldOff size={13} /> Turn off</button>;
  return (
    <form className="two-factor-form" data-testid="two-factor-disable-form" onSubmit={submit}>
      <label>Password<input data-testid="two-factor-disable-password" required type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
      <label>Authenticator or recovery code<input data-testid="two-factor-disable-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></label>
      {error && <p className="form-message error-message">{error}</p>}
      <div className="policy-actions">
        <button className="button button-secondary" type="button" onClick={() => setOpen(false)}>Cancel</button>
        <button className="button button-primary" data-testid="two-factor-disable-confirm" type="submit">Turn off two-factor</button>
      </div>
    </form>
  );
}

function RegenerateCodes() {
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState("");
  async function submit(event) {
    event.preventDefault();
    setError("");
    try {
      setCodes((await api.post("/auth/2fa/recovery-codes", { code })).data.recovery_codes);
      setCode("");
    } catch (e) {
      setError(formatApiError(e));
    }
  }
  if (codes) return <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />;
  return (
    <form className="two-factor-form two-factor-inline" data-testid="regenerate-codes-form" onSubmit={submit}>
      <label>Current authenticator code<input data-testid="regenerate-codes-input" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" /></label>
      <button className="button button-secondary" data-testid="regenerate-codes-button" type="submit"><KeyRound size={14} /> Issue new recovery codes</button>
      {error && <p className="form-message error-message">{error}</p>}
    </form>
  );
}

export default function SecurityPage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setProfile((await api.get("/auth/security")).data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function revokeAll() {
    const response = await api.post("/auth/sessions/revoke-all");
    setMessage(`Signed out of ${response.data.revoked} other session${response.data.revoked === 1 ? "" : "s"}.`);
    load();
  }

  async function revoke(id) {
    await api.delete(`/auth/sessions/${id}`);
    load();
  }

  async function revokeTrusted(hash) {
    try {
      await api.delete(`/auth/trusted-devices/${hash}`);
      setMessage("Trusted device revoked. Sessions on that device now use the 30-minute idle timeout.");
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  async function revokeAllTrusted() {
    const response = await api.post("/auth/trusted-devices/revoke-all");
    setMessage(`Revoked ${response.data.revoked} trusted device${response.data.revoked === 1 ? "" : "s"}.`);
    load();
  }

  async function toggleEnforcement(value) {
    const response = await api.put("/auth/security/settings", { two_factor_enforced: value });
    setProfile((p) => ({ ...p, settings: response.data, two_factor_enforced: response.data.two_factor_enforced }));
    setMessage(value ? "Two-factor sign-in is now required for System Administrator, HR, Operations and Finance." : "Two-factor enforcement is off (two-factor stays optional).");
  }

  const tf = profile?.two_factor;
  return (
    <div className="app-shell" data-testid="security-page">
      <AppSidebar />
      <div className="app-main">
        <AppHeader title="Security" subtitle="Two-factor sign-in, recovery codes, active sessions and your confidentiality acknowledgment." />
        <main className="page-content security-page">
          {message && <p className="form-message success-message" data-testid="security-message">{message}</p>}
          {error && <p className="form-message error-message" data-testid="security-error">{error}</p>}
          {profile && (
            <>
              <section className="inline-edit-panel security-section" data-testid="security-two-factor">
                <div className="section-heading"><div><h2><ShieldCheck size={16} /> Two-factor sign-in</h2><p>{profile.two_factor_required_for_role ? (profile.two_factor_enforced ? "Required for your role." : "Required for your role once enforcement is turned on.") : "Optional for your role, strongly recommended."}</p></div></div>
                {tf?.enabled ? (
                  <div className="security-status" data-testid="two-factor-status-on">
                    <p><strong>On</strong> since {formatStamp(tf.enabled_at)} · {tf.recovery_codes_remaining} recovery code{tf.recovery_codes_remaining === 1 ? "" : "s"} left{tf.recovery_codes_issued_at ? ` (issued ${formatStamp(tf.recovery_codes_issued_at)})` : ""}.</p>
                    <RegenerateCodes />
                    <DisableTwoFactor onDisabled={() => { setMessage("Two-factor sign-in turned off."); load(); updateUser({ two_factor: { enabled: false } }); }} />
                  </div>
                ) : (
                  <TwoFactorSetup required={profile.two_factor_required_for_role} onEnabled={(data, done) => { if (data) updateUser({ two_factor: data.two_factor, two_factor_setup_required: false }); if (done) load(); }} />
                )}
              </section>

              <section className="inline-edit-panel security-section" data-testid="security-sessions">
                <div className="section-heading">
                  <div><h2><MonitorSmartphone size={16} /> Active sessions</h2><p>Staff sessions end after 30 minutes of inactivity{profile.trusted_device_eligible ? " (4 hours on a trusted device), with a 12-hour limit" : ""}; "Keep me signed in" lasts up to 7 days.</p></div>
                  <button className="button button-secondary" data-testid="sign-out-everywhere" type="button" onClick={revokeAll}><LogOut size={14} /> Sign out everywhere</button>
                </div>
                <table className="data-table security-sessions-table">
                  <thead><tr><th>Device</th><th>Signed in</th><th>Last active</th><th>IP</th><th>Expires</th><th></th></tr></thead>
                  <tbody>
                    {profile.sessions.map((s) => (
                      <tr key={s.id} data-testid={`session-row-${s.id}`}>
                        <td>{s.device}{s.current && <span className="status-chip status-active" style={{ marginLeft: 8 }}>This device</span>}{s.trusted_device && <span className="status-chip status-info" style={{ marginLeft: 8 }} data-testid={`session-trusted-${s.id}`}>Trusted · 4 h idle</span>}</td>
                        <td>{formatStamp(s.created_at)}</td><td>{formatStamp(s.last_seen_at)}</td><td>{s.ip}</td><td>{formatStamp(s.expires_at)}</td>
                        <td>{!s.current && <button className="table-edit-button" data-testid={`session-revoke-${s.id}`} type="button" onClick={() => revoke(s.id)}>Sign out</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {profile.trusted_devices && (
                <section className="inline-edit-panel security-section" data-testid="security-trusted-devices">
                  <div className="section-heading">
                    <div><h2><Laptop size={16} /> Trusted devices</h2><p>System Administrators only. A device becomes trusted after a two-factor sign-in with "Trusted device" ticked: idle timeout 4 hours, 12-hour session limit unchanged. Trust lasts {profile.trusted_device_days} days and every trusted sign-in is logged.</p></div>
                    {profile.trusted_devices.length > 1 && <button className="button button-secondary" data-testid="trusted-devices-revoke-all" type="button" onClick={revokeAllTrusted}><ShieldOff size={14} /> Revoke all</button>}
                  </div>
                  {profile.trusted_devices.length === 0 ? (
                    <p className="muted-text" data-testid="trusted-devices-empty">No trusted devices. Tick "Trusted device" on your next two-factor sign-in to add this one.</p>
                  ) : (
                    <table className="data-table security-sessions-table">
                      <thead><tr><th>Device</th><th>Trusted since</th><th>Last sign-in</th><th>IP</th><th>Trust expires</th><th></th></tr></thead>
                      <tbody>
                        {profile.trusted_devices.map((d) => (
                          <tr key={d.device_hash} data-testid={`trusted-device-row-${d.device_hash.slice(0, 12)}`}>
                            <td>{d.label}{d.current && <span className="status-chip status-active" style={{ marginLeft: 8 }}>This device</span>}</td>
                            <td>{formatStamp(d.first_trusted_at)}</td><td>{formatStamp(d.last_used_at)}</td><td>{d.ip}</td><td>{formatStamp(d.expires_at)}</td>
                            <td><button className="table-edit-button" data-testid={`trusted-device-revoke-${d.device_hash.slice(0, 12)}`} type="button" onClick={() => revokeTrusted(d.device_hash)}>Revoke</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              )}

              <section className="inline-edit-panel security-section" data-testid="security-notifications">
                <div className="section-heading"><div><h2><BellRing size={16} /> Notifications</h2><p>Push alerts on your installed TAGForce app. Each category also controls the in-app bell.</p></div></div>
                <NotificationSettings />
              </section>

              <section className="inline-edit-panel security-section" data-testid="security-policy">
                <div className="section-heading"><div><h2><KeyRound size={16} /> Confidentiality and acceptable use</h2><p>Version {profile.policy.version}{profile.policy.acceptance ? ` · accepted ${formatStamp(profile.policy.acceptance.accepted_at)}` : " · not yet accepted"}.</p></div></div>
                <blockquote className="policy-quote" data-testid="security-policy-text">{profile.policy.text}</blockquote>
              </section>

              {profile.settings && (
                <section className="inline-edit-panel security-section" data-testid="security-settings">
                  <div className="section-heading"><div><h2><ShieldCheck size={16} /> Organization security settings</h2><p>System Administrator only.</p></div></div>
                  <label className="policy-check">
                    <input data-testid="two-factor-enforce-toggle" type="checkbox" checked={!!profile.settings.two_factor_enforced} onChange={(e) => toggleEnforcement(e.target.checked)} />
                    <span>Require two-factor sign-in for System Administrator, HR, Operations and Finance (they must set it up at their next sign-in)</span>
                  </label>
                  {profile.settings.updated_at && <p className="pairing-meta">Last changed {formatStamp(profile.settings.updated_at)} by {profile.settings.updated_by}.</p>}
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
